import { TypeOfConsideration } from '../pricing/models/type-of-consideration.enum';
import { ConsiderationTreatment } from '../pricing/models/consideration-treatment.enum';
import { LevelOfService } from '../timeline/models/enums/level-of-service.enum';
import {
    TypesOfConsiderationRow,
    getConsiderationRow,
} from './models/types-of-consideration-row.model';
import { VvConnectFeesDto } from './models/vv-connect-fees.model';

// Per-row fee calculation for the LOI Comparison Consideration Summary.
// DB/FSB rules per #248; SFAS rules per #250, verified against the
// "SFAS Fee Example" sheet (VetValue_Sprint_Planning_v7-07-2026v2.xlsx).

export interface ConsiderationFeeRowInput {
    type: TypeOfConsideration;
    faceValue: number | null | undefined;
    fairValue: number | null | undefined;
    projectedCashFlow: number | null | undefined;
}

export interface SfasFeeParams {
    startLtmRevenue: number | null | undefined;
    ltmRevenueThresholdPercent: number | null | undefined;
    feePercentOnTotalFairValue: number | null | undefined;
    feePercentOnExcessFairValue: number | null | undefined;
}

export interface ConsiderationFeesResult {
    // null fee → the row shows "—" in the Fees column
    feesByType: Partial<Record<TypeOfConsideration, number | null>>;
    totalFees: number | null;
    portionPaidAtClose: number | null;
    // Sum of every row's Projected Cash Flow (incl. Cash at Close) and the fee
    // computed on that total — feed the "Projected Transaction Value/Fee $" rows.
    projectedTransactionValue: number | null;
    projectedTransactionFee: number | null;
}

export const toSfasFeeParams = (
    source:
        | {
              startLtmRevenue?: number | null;
              ltmRevenueThresholdPercent?: number | null;
              feePercentOnTotalFairValue?: number | null;
              feePercentOnExcessFairValue?: number | null;
          }
        | null
        | undefined,
): SfasFeeParams | null =>
    source
        ? {
              startLtmRevenue: source.startLtmRevenue,
              ltmRevenueThresholdPercent: source.ltmRevenueThresholdPercent,
              feePercentOnTotalFairValue: source.feePercentOnTotalFairValue,
              feePercentOnExcessFairValue: source.feePercentOnExcessFairValue,
          }
        : null;

// Mirrors the backend totalCredits in VvConnectFeesCalculationService.cs:
// application + retainer + process + late + suspension credits.
export const getTotalCredits = (fees: VvConnectFeesDto | null | undefined): number =>
    [
        fees?.applicationFee,
        fees?.monthlyRetainerFees,
        fees?.processFees,
        fees?.lateDelayFees,
        fees?.engagementSuspensionFees,
    ].reduce((sum, row) => sum + (row?.credits ?? 0), 0);

// "Net Projected Total Fees" row. DB/FSB: Total Net Fee semantics — the fee
// rows added on top of the projected transaction fee, minus Total Credits.
// SFAS: the example sheet's E25 — Projected Transaction Fee $ minus Total
// Credits only, no other fee rows added.
export const getNetProjectedTotalFees = (
    fees: VvConnectFeesDto | null | undefined,
    projectedTransactionFee: number | null,
    isSfasEngagement: boolean = false,
): number | null => {
    if (projectedTransactionFee == null) {
        return null;
    }
    if (isSfasEngagement) {
        return projectedTransactionFee - getTotalCredits(fees);
    }
    const otherFees = [
        fees?.applicationFee,
        fees?.monthlyRetainerFees,
        fees?.processFees,
        fees?.lateDelayFees,
        fees?.engagementSuspensionFees,
    ].reduce((sum, row) => sum + (row?.fee ?? 0), 0);
    return projectedTransactionFee + otherFees - getTotalCredits(fees);
};

export const isAtCloseTiming = (timing: ConsiderationTreatment | null | undefined): boolean =>
    timing === ConsiderationTreatment.FaceValueAtClosing ||
    timing === ConsiderationTreatment.FairValueAtClosing;

const getTiming = (
    considerationRows: TypesOfConsiderationRow[],
    type: TypeOfConsideration,
): ConsiderationTreatment | null =>
    getConsiderationRow({ considerationRows }, type)?.feePaymentTiming ?? null;

const sumOrNull = (values: (number | null | undefined)[]): number | null => {
    const present = values.filter((v): v is number => v != null);
    return present.length === 0 ? null : present.reduce((s, v) => s + v, 0);
};

const calculateDbFsbFees = (
    rows: ConsiderationFeeRowInput[],
    considerationRows: TypesOfConsiderationRow[],
    vvConnectFees: VvConnectFeesDto | null | undefined,
): ConsiderationFeesResult => {
    const feePercent = vvConnectFees?.transactionFeePercent?.fee ?? null;
    const feesByType: Partial<Record<TypeOfConsideration, number | null>> = {};
    let atCloseFeesSum = 0;

    rows.forEach((row) => {
        if (row.type === TypeOfConsideration.CashAtClose) {
            // Cash at Close is inherently paid at close — its own fee joins the
            // pooled Portion Paid at Close regardless of any stored timing. Base
            // is the projected cash flow (fair value else cash amount), matching
            // the SFAS branch and the Projected Transaction Value sum.
            if (row.projectedCashFlow != null && feePercent != null) {
                atCloseFeesSum += (row.projectedCashFlow * feePercent) / 100;
            }
            return;
        }
        const timing = getTiming(considerationRows, row.type);
        const base =
            timing === ConsiderationTreatment.FaceValueAtClosing
                ? row.faceValue
                : timing === ConsiderationTreatment.FairValueAtClosing
                    ? row.fairValue
                    : row.projectedCashFlow;
        const fee = base != null && feePercent != null ? (base * feePercent) / 100 : null;
        if (isAtCloseTiming(timing)) {
            // At-close fees fold into the pooled Portion Paid at Close shown on
            // the Cash at Close row — no standalone fee in this row's Fees cell.
            feesByType[row.type] = null;
            if (fee != null) {
                atCloseFeesSum += fee;
            }
        } else {
            feesByType[row.type] = fee;
        }
    });

    const portionPaidAtClose =
        feePercent == null
            ? null
            : Math.max(0, atCloseFeesSum - getTotalCredits(vvConnectFees));
    feesByType[TypeOfConsideration.CashAtClose] = portionPaidAtClose;

    const projectedTransactionValue = sumOrNull(rows.map((row) => row.projectedCashFlow));
    const projectedTransactionFee =
        feePercent != null && projectedTransactionValue != null
            ? (projectedTransactionValue * feePercent) / 100
            : null;

    return {
        feesByType,
        totalFees: sumOrNull(Object.values(feesByType)),
        portionPaidAtClose,
        projectedTransactionValue,
        projectedTransactionFee,
    };
};

// #250 — SFAS, per the "SFAS Fee Example" sheet:
//   threshold        = startLtmRevenue × ltmRevenueThresholdPercent%
//   greaterOf(v)     = MAX(totalPct% × v, excessPct% × (v − threshold))
//   total fee        = greaterOf(Σ projected cash flow of all rows)
//   net total fee    = total fee − Total Credits (the sheet's E25 — no other
//                      fee rows added)                     → the Total Value row
//   portion at close = greaterOf(Σ PCF of at-close rows) − Total Credits
//                                                          → the Cash at Close row
//   deferred portion = net total fee − portion at close, allocated across the
//                      receipt-timing rows proportionally to their PCF (credits
//                      cancel out of this difference, matching the sheet's E28).
//   at-close rows other than Cash at Close show no own fee (folded into the portion).
const calculateSfasFees = (
    rows: ConsiderationFeeRowInput[],
    considerationRows: TypesOfConsiderationRow[],
    vvConnectFees: VvConnectFeesDto | null | undefined,
    sfasParams: SfasFeeParams | null,
): ConsiderationFeesResult => {
    const noFees: ConsiderationFeesResult = {
        feesByType: {},
        totalFees: null,
        portionPaidAtClose: null,
        projectedTransactionValue: null,
        projectedTransactionFee: null,
    };
    if (
        sfasParams?.startLtmRevenue == null ||
        sfasParams.ltmRevenueThresholdPercent == null ||
        sfasParams.feePercentOnTotalFairValue == null ||
        sfasParams.feePercentOnExcessFairValue == null
    ) {
        return noFees;
    }

    const threshold =
        (sfasParams.startLtmRevenue * sfasParams.ltmRevenueThresholdPercent) / 100;
    const totalPct = sfasParams.feePercentOnTotalFairValue / 100;
    const excessPct = sfasParams.feePercentOnExcessFairValue / 100;
    const greaterOf = (value: number): number =>
        Math.max(totalPct * value, excessPct * (value - threshold));

    const isRowAtClose = (row: ConsiderationFeeRowInput): boolean =>
        row.type === TypeOfConsideration.CashAtClose ||
        isAtCloseTiming(getTiming(considerationRows, row.type));

    const getRowPcf = (row: ConsiderationFeeRowInput): number => {
        if (
            row.type !== TypeOfConsideration.CashAtClose &&
            getTiming(considerationRows, row.type) === ConsiderationTreatment.FairValueAtClosing
        ) {
            return row.fairValue ?? 0;
        }
        return row.projectedCashFlow ?? 0;
    };

    const totalPcf = rows.reduce((sum, row) => sum + getRowPcf(row), 0);
    if (totalPcf <= 0) {
        return noFees;
    }

    const totalCredits = getTotalCredits(vvConnectFees);
    const netTotalFee = getNetProjectedTotalFees(vvConnectFees, greaterOf(totalPcf), true);

    const atClosePcf = rows
        .filter(isRowAtClose)
        .reduce((sum, row) => sum + getRowPcf(row), 0);
    const portionPaidAtClose = Math.max(0, greaterOf(atClosePcf) - totalCredits);
    const deferredPortion = netTotalFee - portionPaidAtClose;

    const receiptRows = rows.filter(
        (row) => !isRowAtClose(row) && (row.projectedCashFlow ?? 0) > 0,
    );
    const receiptPcfSum = receiptRows.reduce(
        (sum, row) => sum + (row.projectedCashFlow ?? 0),
        0,
    );

    const feesByType: Partial<Record<TypeOfConsideration, number | null>> = {};
    rows.forEach((row) => {
        feesByType[row.type] = null;
    });
    feesByType[TypeOfConsideration.CashAtClose] = portionPaidAtClose;
    receiptRows.forEach((row) => {
        feesByType[row.type] =
            receiptPcfSum > 0
                ? ((row.projectedCashFlow ?? 0) / receiptPcfSum) * deferredPortion
                : null;
    });

    return {
        feesByType,
        totalFees: netTotalFee,
        portionPaidAtClose,
        projectedTransactionValue: totalPcf,
        projectedTransactionFee: greaterOf(totalPcf),
    };
};

export const calculateConsiderationFees = (
    rows: ConsiderationFeeRowInput[],
    considerationRows: TypesOfConsiderationRow[],
    vvConnectFees: VvConnectFeesDto | null | undefined,
    levelOfService: number,
    sfasParams: SfasFeeParams | null = null,
): ConsiderationFeesResult =>
    levelOfService === LevelOfService.StrategicFinancialAdvisoryService
        ? calculateSfasFees(rows, considerationRows, vvConnectFees, sfasParams)
        : calculateDbFsbFees(rows, considerationRows, vvConnectFees);
